/**
 * Analytics & History Dashboard Component
 * 
 * Provides:
 * - Daily/Weekly P&L Charts
 * - Win Rate Analysis by Symbol
 * - Best/Worst Trade Analysis
 * - Trade History Table
 */

import React, { useState, useEffect, useMemo, memo } from 'react';
import { createChart, LineSeries, HistogramSeries, ColorType } from 'lightweight-charts';
import { TrendingUp, TrendingDown, Target, Award, AlertTriangle, Calendar, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Chart theme
const chartTheme = {
  layout: {
    background: { type: ColorType.Solid, color: '#0e1117' },
    textColor: '#8b949e',
  },
  grid: {
    vertLines: { color: '#21262d' },
    horzLines: { color: '#21262d' },
  },
  rightPriceScale: {
    borderColor: '#30363d',
  },
  timeScale: {
    borderColor: '#30363d',
    timeVisible: true,
  },
};

// ============================================
// P&L CHART COMPONENT
// ============================================

const PnLChart = memo(({ data, period = 'daily' }) => {
  const chartContainerRef = React.useRef(null);
  const chartRef = React.useRef(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data || data.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      ...chartTheme,
      width: chartContainerRef.current.clientWidth,
      height: 200,
    });

    // Cumulative P&L line
    const lineSeries = chart.addSeries(LineSeries, {
      color: '#58a6ff',
      lineWidth: 2,
      title: 'Cumulative P&L',
    });

    // Daily P&L histogram
    const histogramSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'price' },
      priceScaleId: 'right',
    });

    // Process data
    let cumulative = 0;
    const lineData = [];
    const histData = [];

    data.forEach(d => {
      cumulative += d.pnl;
      lineData.push({ time: d.time, value: cumulative });
      histData.push({
        time: d.time,
        value: d.pnl,
        color: d.pnl >= 0 ? '#22c55e' : '#ef4444',
      });
    });

    lineSeries.setData(lineData);
    histogramSeries.setData(histData);

    chart.timeScale().fitContent();

    chartRef.current = chart;

    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length === 0 || !chartRef.current) return;
      const { width } = entries[0].contentRect;
      chartRef.current.applyOptions({ width });
    });

    resizeObserver.observe(chartContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [data, period]);

  return (
    <div ref={chartContainerRef} className="w-full h-[200px]" data-testid="pnl-chart" />
  );
});

PnLChart.displayName = 'PnLChart';


// ============================================
// STATS CARD COMPONENT
// ============================================

const StatsCard = ({ icon: Icon, label, value, subValue, trend, color = 'accent' }) => (
  <div className="bg-surface border border-border rounded-lg p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 text-${color}`} />
      <span className="text-xs text-muted-foreground uppercase">{label}</span>
    </div>
    <div className={`text-2xl font-bold font-mono text-${color}`}>
      {value}
    </div>
    {subValue && (
      <div className="text-xs text-muted-foreground mt-1">{subValue}</div>
    )}
    {trend !== undefined && (
      <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-buy' : 'text-sell'}`}>
        {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        <span>{trend >= 0 ? '+' : ''}{trend}%</span>
      </div>
    )}
  </div>
);


// ============================================
// TRADE HISTORY TABLE COMPONENT
// ============================================

const TradeHistoryTable = memo(({ trades }) => {
  if (!trades || trades.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground">
        No trades recorded yet
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th className="py-2 px-2 text-left">Time</th>
            <th className="py-2 px-2 text-left">Symbol</th>
            <th className="py-2 px-2 text-left">Direction</th>
            <th className="py-2 px-2 text-right">Entry</th>
            <th className="py-2 px-2 text-right">Exit</th>
            <th className="py-2 px-2 text-right">P&L</th>
            <th className="py-2 px-2 text-left">Exit Reason</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade, i) => (
            <tr key={trade.trade_id || i} className="border-b border-border/50 hover:bg-surface-highlight">
              <td className="py-2 px-2 text-muted-foreground">
                {trade.entry_time && !isNaN(trade.entry_time) 
                  ? new Date(trade.entry_time * 1000).toLocaleTimeString('en-IN', { hour12: false })
                  : '-'}
              </td>
              <td className="py-2 px-2">{trade.symbol}</td>
              <td className={`py-2 px-2 ${trade.direction === 'LONG' ? 'text-buy' : 'text-sell'}`}>
                {trade.direction}
              </td>
              <td className="py-2 px-2 text-right">{trade.entry_price?.toFixed(2)}</td>
              <td className="py-2 px-2 text-right">{trade.exit_price?.toFixed(2) || '-'}</td>
              <td className={`py-2 px-2 text-right font-bold ${trade.pnl >= 0 ? 'text-buy' : 'text-sell'}`}>
                {trade.pnl >= 0 ? '+' : ''}{trade.pnl?.toFixed(2)}
              </td>
              <td className="py-2 px-2 text-muted-foreground truncate max-w-[150px]" title={trade.exit_reason}>
                {trade.exit_reason || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

TradeHistoryTable.displayName = 'TradeHistoryTable';


// ============================================
// MAIN ANALYTICS DASHBOARD COMPONENT
// ============================================

export const AnalyticsDashboard = () => {
  const [tradeHistory, setTradeHistory] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalPnL: 0,
    totalPnLNet: 0,
    winRate: 0,
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    avgWin: 0,
    avgLoss: 0,
    profitFactor: 0,
    bestTrade: null,
    worstTrade: null,
    dailyData: [],
  });
  const [executionCosts, setExecutionCosts] = useState({
    estimatedSlippage: 50,
    totalSlippagePaid: 0
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('daily'); // 'daily', 'weekly', 'monthly'
  const [symbol, setSymbol] = useState('ALL');
  const [days, setDays] = useState(30);

  // SESSION 70: Fetch historical P&L from new endpoint
  const fetchHistoricalPnL = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const symbolParam = symbol !== 'ALL' ? `&symbol=${symbol}` : '';
      const res = await fetch(`${API}/api/signals/historical-pnl?days=${days}${symbolParam}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Set trade history
        setTradeHistory(data.trades || []);
        
        // Set pre-calculated analytics from backend
        const backendAnalytics = data.analytics || {};
        setAnalytics({
          totalPnL: backendAnalytics.total_pnl || 0,
          totalPnLNet: backendAnalytics.total_pnl_net || 0,
          winRate: backendAnalytics.win_rate || 0,
          totalTrades: backendAnalytics.total_trades || 0,
          winningTrades: backendAnalytics.winning_trades || 0,
          losingTrades: backendAnalytics.losing_trades || 0,
          avgWin: backendAnalytics.avg_win || 0,
          avgLoss: backendAnalytics.avg_loss || 0,
          profitFactor: backendAnalytics.profit_factor || 0,
          bestTrade: backendAnalytics.best_trade,
          worstTrade: backendAnalytics.worst_trade,
          dailyData: data.daily_data || [],
        });
        
        // Set execution costs info
        const costs = data.execution_costs || {};
        setExecutionCosts({
          estimatedSlippage: costs.estimated_slippage || 50,
          totalSlippagePaid: costs.total_slippage_paid || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch historical P&L:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchHistoricalPnL();
  }, [symbol, days]);

  // Calculate streaks from trade history (client-side)
  const streakData = useMemo(() => {
    if (!tradeHistory || tradeHistory.length === 0) {
      return {
        streakCurrent: 0,
        maxWinStreak: 0,
        maxLossStreak: 0,
      };
    }

    // Use net_pnl for streak calculation
    let currentStreak = 0;
    let maxWinStreak = 0;
    let maxLossStreak = 0;
    let tempWinStreak = 0;
    let tempLossStreak = 0;

    tradeHistory.forEach(t => {
      const pnl = t.net_pnl ?? t.pnl ?? 0;
      if (pnl > 0) {
        tempWinStreak++;
        tempLossStreak = 0;
        maxWinStreak = Math.max(maxWinStreak, tempWinStreak);
      } else {
        tempLossStreak++;
        tempWinStreak = 0;
        maxLossStreak = Math.max(maxLossStreak, tempLossStreak);
      }
    });

    // Current streak
    if (tradeHistory.length > 0) {
      const lastTrade = tradeHistory[0]; // Most recent first
      const lastPnl = lastTrade.net_pnl ?? lastTrade.pnl ?? 0;
      currentStreak = lastPnl > 0 ? tempWinStreak : -tempLossStreak;
    }

    return {
      streakCurrent: currentStreak,
      maxWinStreak,
      maxLossStreak,
    };
  }, [tradeHistory]);

  // Get unique symbols
  const symbols = useMemo(() => {
    const uniqueSymbols = [...new Set(tradeHistory.map(t => t.symbol))];
    return ['ALL', ...uniqueSymbols];
  }, [tradeHistory]);

  return (
    <div className="space-y-4 p-4" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">📊 Analytics & History</h2>
          <p className="text-sm text-muted-foreground">Trade performance analysis (Net P&L = Gross - ₹{executionCosts.estimatedSlippage} slippage)</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Days Filter */}
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="bg-surface border border-border rounded px-3 py-1.5 text-sm"
            data-testid="days-filter"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          {/* Symbol Filter */}
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="bg-surface border border-border rounded px-3 py-1.5 text-sm"
            data-testid="symbol-filter"
          >
            {symbols.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHistoricalPnL}
            disabled={loading}
            data-testid="refresh-analytics"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Grid - SESSION 70: Using Net P&L */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatsCard
          icon={TrendingUp}
          label="Total P&L (Net)"
          value={`${analytics.totalPnLNet >= 0 ? '+' : ''}₹${analytics.totalPnLNet.toFixed(0)}`}
          subValue={`${analytics.totalTrades} trades`}
          color={analytics.totalPnLNet >= 0 ? 'buy' : 'sell'}
        />
        <StatsCard
          icon={Target}
          label="Win Rate"
          value={`${analytics.winRate.toFixed(1)}%`}
          subValue={`${analytics.winningTrades}W / ${analytics.losingTrades}L`}
          color={analytics.winRate >= 50 ? 'buy' : 'sell'}
        />
        <StatsCard
          icon={Award}
          label="Profit Factor"
          value={analytics.profitFactor.toFixed(2)}
          subValue={analytics.profitFactor >= 1.5 ? 'Excellent' : analytics.profitFactor >= 1 ? 'Good' : 'Poor'}
          color={analytics.profitFactor >= 1.5 ? 'buy' : analytics.profitFactor >= 1 ? 'warning' : 'sell'}
        />
        <StatsCard
          icon={TrendingUp}
          label="Avg Win"
          value={`+₹${analytics.avgWin.toFixed(0)}`}
          color="buy"
        />
        <StatsCard
          icon={TrendingDown}
          label="Avg Loss"
          value={`-₹${analytics.avgLoss.toFixed(0)}`}
          color="sell"
        />
        <StatsCard
          icon={AlertTriangle}
          label="Streak"
          value={`${streakData.streakCurrent > 0 ? '+' : ''}${streakData.streakCurrent}`}
          subValue={`Max W: ${streakData.maxWinStreak} | Max L: ${streakData.maxLossStreak}`}
          color={streakData.streakCurrent > 0 ? 'buy' : streakData.streakCurrent < 0 ? 'sell' : 'muted-foreground'}
        />
      </div>

      {/* P&L Chart */}
      <div className="panel p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-accent" />
            P&L Over Time
          </h3>
          {executionCosts.totalSlippagePaid > 0 && (
            <span className="text-xs text-muted-foreground">
              Total fees paid: ₹{executionCosts.totalSlippagePaid.toFixed(0)}
            </span>
          )}
        </div>
        {analytics.dailyData.length > 0 ? (
          <PnLChart data={analytics.dailyData} period={period} />
        ) : (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            No trade data available for the selected period
          </div>
        )}
      </div>

      {/* Best/Worst Trade Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="panel p-4">
          <h4 className="text-sm font-semibold text-buy mb-2 flex items-center gap-2">
            <Award className="w-4 h-4" /> Best Trade
          </h4>
          {analytics.bestTrade ? (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Symbol:</span>
                <span className="font-mono">{analytics.bestTrade.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Direction:</span>
                <span className={analytics.bestTrade.signal?.includes('BUY') ? 'text-buy' : 'text-sell'}>
                  {analytics.bestTrade.signal}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Net P&L:</span>
                <span className="font-bold text-buy font-mono">+₹{(analytics.bestTrade.net_pnl || analytics.bestTrade.pnl)?.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No trades yet</p>
          )}
        </div>

        <div className="panel p-4">
          <h4 className="text-sm font-semibold text-sell mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Worst Trade
          </h4>
          {analytics.worstTrade && analytics.worstTrade.net_pnl < 0 ? (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Symbol:</span>
                <span className="font-mono">{analytics.worstTrade.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Direction:</span>
                <span className={analytics.worstTrade.signal?.includes('BUY') ? 'text-buy' : 'text-sell'}>
                  {analytics.worstTrade.signal}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Net P&L:</span>
                <span className="font-bold text-sell font-mono">₹{(analytics.worstTrade.net_pnl || analytics.worstTrade.pnl)?.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No losing trades</p>
          )}
        </div>
      </div>

      {/* Trade History Table - SESSION 70: Shows closed trades from signal_history */}
      <div className="panel p-4">
        <h3 className="text-lg font-semibold mb-4">Trade History</h3>
        <TradeHistoryTable trades={tradeHistory} />
      </div>
    </div>
  );
};
